import { UseFormReturnType } from "@mantine/form"
import { useTranslation } from "react-i18next"

import { CountrySelectField } from "~/pages/Admin/components/Fields/CountrySelectField"
import { DateInputField } from "~/pages/Admin/components/Fields/DateInputField"
import { LanguageSelectField } from "~/pages/Admin/components/Fields/LanguageSelectField"
import { MultiSelectField } from "~/pages/Admin/components/Fields/MultiSelectField"
import { ReferenceInputField } from "~/pages/Admin/components/Fields/ReferenceField"
import { SelectField } from "~/pages/Admin/components/Fields/SelectField"
import SelectListRegistrationId from "~/pages/Admin/components/Fields/SelectListRegistrationId"
import { SwitchField } from "~/pages/Admin/components/Fields/SwitchField"
import { TagsInputField } from "~/pages/Admin/components/Fields/TagsInputField"
import { TextInputField } from "~/pages/Admin/components/Fields/TextInputField"
import {
  errorCodes,
  errorCodesEnum,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap"

import { CoordinatesInputField } from "./Fields/CoordinatesInputField"
import { FormFieldConfig } from "./TypesForm"

const isErrorCodeKey = (key: string): key is keyof errorCodes => {
  return Object.keys(errorCodesEnum).includes(key)
}
const getErrorWord = (form: any, fields: FormFieldConfig) => {
  const hasUnknownError =
    form.errors[fields.name] && !isErrorCodeKey(form.errors[fields.name])
  const hasErrorCode = isErrorCodeKey(form.errors[fields.name])

  if (hasUnknownError) {
    return form.errors[fields.name]
  }
  if (hasErrorCode) {
    return form.errors[fields.name]
  }
  return undefined
}

type RenderFieldProps = {
  field: FormFieldConfig
  form: UseFormReturnType<any>
}

const INPUTS = {
  text: TextInputField,
  select: SelectField,
  multiselect: MultiSelectField,
  countrySelect: CountrySelectField,
  languageSelect: LanguageSelectField,
  tagsInput: TagsInputField,
  selectListRegistrationId: SelectListRegistrationId,
  date: DateInputField,
  switch: SwitchField,
  reference: ReferenceInputField,
  coordinates: CoordinatesInputField,
  default: () => null,
}

const getField = (
  field: FormFieldConfig,
  form: UseFormReturnType<any>,
  error?: keyof errorCodes,
  translations?: errorCodes
) => {
  const inputRenderer =
    INPUTS[field.type as keyof typeof INPUTS] ?? INPUTS.default

  return inputRenderer(field, form, error, translations) || INPUTS.default()
}

export const RenderField = ({ field, form }: RenderFieldProps) => {
  const { t } = useTranslation("common")
  const error: keyof errorCodes | undefined = getErrorWord(form, field)

  const translations = t(`zodErrors`, {
    returnObjects: true,
  }) as unknown as errorCodes

  return getField(field, form, error, translations)
}
